import { PaletteCommand } from './command-palette.model';
import { scoreSubstring, scoreTerm } from 'app/core/search/rank-matches';

/**
 * Query matching for the command palette.
 *
 * The scoring primitives live in `core/search/rank-matches.ts`, shared with the
 * projects page and the header's project switcher. What is specific here is how
 * strictly each field is matched — and that turns out to matter a lot, because
 * the palette searches many projects' pages at once.
 */

/**
 * Score a command against a query.
 *
 * `0` for an empty query (everything matches, registry order is kept), a
 * positive number for a match, and `-1` when any term is missing entirely —
 * every term must land somewhere, which is what makes `alpine servers` narrow
 * instead of widen.
 *
 * The fields are matched at two different strictnesses, on purpose:
 *
 * - **`title`** allows the subsequence fallback, so `srvcfg` still finds
 *   "Server Configs". Titles are two or three words, so a scattered match there
 *   is still recognisably the thing you meant.
 * - **everything else** must contain the term literally. These were previously
 *   concatenated and matched with the same fallback, and over a haystack that
 *   long a subsequence means nothing: `alpine` "matched" *Cortex Analytics* in
 *   a project called nginx, and once the palette held every project's pages
 *   that turned a cross-project search into noise.
 *
 * `appName` being in here is what lets a query name a project at all;
 * `group` carries the product, so `fleet serv` narrows to Fleet's server pages.
 */
export function scoreCommand(query: string, command: PaletteCommand): number {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return 0;

  const title = command.title.toLowerCase();
  const literalFields = [
    command.group,
    command.appName ?? '',
    command.meta ?? '',
    command.keywords ?? '',
  ]
    .join(' ')
    .toLowerCase();

  let total = 0;

  for (const term of terms) {
    // The title counts double, so a title hit outranks a command that merely
    // mentions the term in its keywords.
    const best = Math.max(
      scoreTerm(term, title) * 2,
      scoreSubstring(term, literalFields),
    );
    if (best < 0) return -1;
    total += best;
  }

  return total;
}
